import { useEffect, useState } from "react";
import { Checkbox, FormControlLabel, Grid2 } from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import BaseSettingsPage from "@settings/base/BaseSettingsPage";
import SettingsCard from "../../base/SettingsCard";
import { SettingsMultiselect } from "../../components/forms/SettingsMultiselect";
import { UPDATE_USER_PROFILE } from "@company/graphql/mutations/updateUserProfile";
import { FETCH_USER_PROFILE } from "@company/graphql/queries/fetchUserProfile"; 
import { Mutation, MutationUpdateUserArgs, Query, UserChatSetting_Language, UserChatSetting_ResponseLength, UserChatSetting_ResponseStyle, UserChatSetting_Verbosity, UserChatSettings } from "@/app/graphql/codegen";

type ExtendedUserChatSettings = UserChatSettings & { advancedSettingsEnabled?: boolean };

const defaultSettings: ExtendedUserChatSettings = {
  language: UserChatSetting_Language.De,
  verbosity: UserChatSetting_Verbosity.Fluent,
  responseLength: UserChatSetting_ResponseLength.Medium,
  responseStyle: UserChatSetting_ResponseStyle.Scientific,
  showAmendments: false,
  showOriginals: false,
  showSummaries: false,
  advancedSettingsEnabled: false,
};

export const ChatSettingsPage = () => {
  const { data, loading } = useQuery<Pick<Query, "userProfile">>(FETCH_USER_PROFILE);
  const [updateProfile] = useMutation<Pick<Mutation, "updateUser">, MutationUpdateUserArgs>(UPDATE_USER_PROFILE);
  
  const [settings, setSettings] = useState<ExtendedUserChatSettings>(defaultSettings);
  const [hasChanges, setHasChanges] = useState(false);

  
  const languageOptions = [
    { label: "Deutsch", value: UserChatSetting_Language.De },
    { label: "English", value: UserChatSetting_Language.En }
  ];

  const verbosityOptions = [
    { label: "Stichpunkte", value: UserChatSetting_Verbosity.BulletPoints },
    { label: "Fließtext", value: UserChatSetting_Verbosity.Fluent }
  ];

  const responseLengthOptions = [
    { label: "Kurz", value: UserChatSetting_ResponseLength.Short },
    { label: "Mittel", value: UserChatSetting_ResponseLength.Medium },
    { label: "Lang", value: UserChatSetting_ResponseLength.Long }
  ];

  const responseStyleOptions = [
    { label: "Wissenschaftlich", value: UserChatSetting_ResponseStyle.Scientific },
    { label: "Umgangssprachlich", value: UserChatSetting_ResponseStyle.Casual }
  ];


  useEffect(() => {
    if (data?.userProfile?.chatSettings) {
      setSettings({
        ...defaultSettings,
        ...(data.userProfile.chatSettings as any)
      } as ExtendedUserChatSettings);
    }
  }, [data]);

  const handleSettingChange = (key: keyof ExtendedUserChatSettings) => (value: string | boolean) => {
    setSettings((prev: ExtendedUserChatSettings) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!data?.userProfile?.id) return;
    
    try {
      await updateProfile({
        variables: {
          id: data.userProfile.id,
          chatSettings: settings
        },
        refetchQueries: [{ query: FETCH_USER_PROFILE }]
      });
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save chat settings:", error);
    }
  };

  const oneSettingUndefined = Object.values(settings).some(value => value === undefined);

  return (
    <BaseSettingsPage 
      title="Chateinstellungen" 
      onSave={handleSave}
      saveDisabled={!hasChanges || loading || oneSettingUndefined}
      hasChanges={hasChanges}
      useOuterScroll
    >
      <SettingsCard title="Grundeinstellungen">
        <Grid2 size={12} sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.advancedSettingsEnabled || false}
                onChange={(e) => handleSettingChange("advancedSettingsEnabled")(e.target.checked)}
                disabled={loading}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  mt: 0,
                  '& .MuiSvgIcon-root': { fontSize: 18 }
                }}
              />
            }
            label="Erweiterte Einstellungen aktivieren"
            sx={{ width: '100%', mb: 1 }}
          />
        </Grid2>
        <SettingsMultiselect
          title="Sprache"
          options={languageOptions}
          value={settings.language!} // ich erlaube mir das "!", da mit "oneSettingUndefined" geprüft wird, ob ein Wert undefined ist
          onChange={handleSettingChange("language")}
          disabled={loading}
        />
        <SettingsMultiselect
          title="Ausführlichkeit"
          options={verbosityOptions}
          value={settings.verbosity!}
          onChange={handleSettingChange("verbosity")}
          disabled={loading}
        />
        <SettingsMultiselect
          title="Antwortlänge"
          options={responseLengthOptions}
          value={settings.responseLength!}
          onChange={handleSettingChange("responseLength")}
          disabled={loading}
        />
        <SettingsMultiselect
          title="Sprachlicher Stil"
          options={responseStyleOptions}
          value={settings.responseStyle!}
          onChange={handleSettingChange("responseStyle")}
          disabled={loading}
        />
      </SettingsCard>
    </BaseSettingsPage>
  );
};